/**
 * Dual-currency display helpers for surfaces that already have resolved amounts (no FX in UI).
 * USD headline for Dynamic Packages matches legacy ceil-from-ILS behavior (see vacation package cards).
 */

function toFinitePositive(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Resolve raw USD from an SSOT ILS amount (ilsToUsd preferred, else currencyRate.USD).
 * @param {number|null|undefined} ilsAmount
 * @param {(n: number, fixed?: boolean) => number} [ilsToUsd]
 * @param {{ USD?: number }|null|undefined} [currencyRate]
 * @returns {number|null}
 */
function resolveUsdRawFromIls(ilsAmount, ilsToUsd, currencyRate) {
  const ils = toFinitePositive(ilsAmount);
  if (ils == null) return null;
  const usdFromFn =
    typeof ilsToUsd === "function" ? Number(ilsToUsd(ils, false)) : NaN;
  const usdRate = Number(currencyRate?.USD);
  const usdFromRate =
    Number.isFinite(usdRate) && usdRate > 0 ? ils / usdRate : NaN;
  const usdRaw =
    Number.isFinite(usdFromFn) && usdFromFn > 0
      ? usdFromFn
      : Number.isFinite(usdFromRate) && usdFromRate > 0
        ? usdFromRate
        : null;
  if (usdRaw == null || usdRaw <= 0) return null;
  return usdRaw;
}

/**
 * Resolve USD integer (ceiling) from an SSOT ILS package total — same rules as legacy card headline.
 * @param {number|null|undefined} ilsAmount
 * @param {(n: number, fixed?: boolean) => number} [ilsToUsd]
 * @param {{ USD?: number }|null|undefined} [currencyRate]
 * @returns {number|null}
 */
export function resolveUsdCeilFromIls(ilsAmount, ilsToUsd, currencyRate) {
  const usdRaw = resolveUsdRawFromIls(ilsAmount, ilsToUsd, currencyRate);
  if (usdRaw == null) return null;
  return Math.ceil(usdRaw);
}

/**
 * Resolve USD integer (floor) from ILS — baggage/luggage dual-currency card display.
 * @param {number|null|undefined} ilsAmount
 * @param {(n: number, fixed?: boolean) => number} [ilsToUsd]
 * @param {{ USD?: number }|null|undefined} [currencyRate]
 * @returns {number|null}
 */
export function resolveUsdFloorFromIls(ilsAmount, ilsToUsd, currencyRate) {
  const usdRaw = resolveUsdRawFromIls(ilsAmount, ilsToUsd, currencyRate);
  if (usdRaw == null) return null;
  const floored = Math.floor(usdRaw);
  return floored > 0 ? floored : null;
}

export function formatUsdIntegerDisplay(usdInteger) {
  const u = toFinitePositive(usdInteger);
  if (u == null) return null;
  const n = Math.floor(Number(usdInteger));
  try {
    return `$${n.toLocaleString("en-US")}`;
  } catch {
    return `$${String(n)}`;
  }
}

export function formatIlsIntegerDisplay(ilsAmount) {
  const i = toFinitePositive(ilsAmount);
  if (i == null) return null;
  const n = Math.round(Number(ilsAmount));
  try {
    return `₪${n.toLocaleString("he-IL")}`;
  } catch {
    return `₪${String(n)}`;
  }
}

/**
 * @param {{ usd?: number|null, ils?: number|null, primaryCurrency: 'USD' | 'ILS' }} params
 * @returns {{ primaryFormatted: string, secondaryFormatted: string|null, primaryCurrency: string }|null}
 */
export function formatDualCurrencyDisplay({ usd, ils, primaryCurrency }) {
  const u = toFinitePositive(usd);
  const i = toFinitePositive(ils);
  if (primaryCurrency === "USD") {
    if (u == null) return null;
    const primaryFormatted = formatUsdIntegerDisplay(u);
    if (!primaryFormatted) return null;
    const secondaryFormatted = i != null ? `(${formatIlsIntegerDisplay(i)})` : null;
    return { primaryFormatted, secondaryFormatted, primaryCurrency };
  }
  if (primaryCurrency === "ILS") {
    if (i == null) return null;
    const primaryFormatted = formatIlsIntegerDisplay(i);
    if (!primaryFormatted) return null;
    const secondaryFormatted = u != null ? `(${formatUsdIntegerDisplay(u)})` : null;
    return { primaryFormatted, secondaryFormatted, primaryCurrency };
  }
  return null;
}

/**
 * Single-line HotelsRiver baggage card display: `₪216 ($70)`.
 * ILS rounded for display; USD floored via {@link resolveUsdFloorFromIls}.
 * Display-only — does not change ledger / totals amounts (those keep ceil).
 *
 * @param {number|null|undefined} ilsAmount
 * @param {{ ilsToUsd?: (n: number, fixed?: boolean) => number, currencyRate?: { USD?: number } | null }} [deps]
 * @returns {string|null}
 */
export function formatIlsPrimaryWithUsdParentheticalLine(ilsAmount, deps = {}) {
  const ils = Number(ilsAmount);
  if (!Number.isFinite(ils) || ils <= 0) return null;
  const ilsRounded = Math.round(ils);
  const usdFloor = resolveUsdFloorFromIls(ilsRounded, deps.ilsToUsd, deps.currencyRate);
  const dual = formatDualCurrencyDisplay({
    usd: usdFloor,
    ils: ilsRounded,
    primaryCurrency: "ILS",
  });
  if (!dual?.primaryFormatted) return null;
  return dual.secondaryFormatted
    ? `${dual.primaryFormatted} ${dual.secondaryFormatted}`
    : dual.primaryFormatted;
}
